<template>
  <div>
    <vs-dialog v-model="show" blur prevent-close not-close>
    <template #header>
      <h4>Importazione Massiva POI</h4>
    </template>
    <div>
      <vs-row align="center" justify="space-between" style="margin-bottom: 16px;">
<vs-button @click="downloadExample" icon size="large">
  <i class="bx bx-download"></i>
  Scarica XLSX di esempio
</vs-button>
<label class="vs-upload-label">
  <vs-button icon size="large" @click="$refs.fileInput.click()">
    <i class="bx bx-upload"></i>
    Carica XLSX
  </vs-button>
  <input type="file" accept=".xlsx" @change="onFileChange" style="display: none;" ref="fileInput" />
</label>
      </vs-row>

      <div v-if="parseErrors.length" style="margin-top: 10px;">
        <vs-alert color="danger" icon="warning" v-for="err in parseErrors" :key="err" style="margin-bottom: 4px;">
          {{ err }}
        </vs-alert>
      </div>

      <div v-if="parsedRows.length" style="margin-top: 24px;">
        <div v-if="importing" style="margin-bottom: 12px;">
          <vs-progress :percent="progress" color="primary" style="width: 100%; margin-bottom: 8px;" />
          <div style="font-size: 14px;">{{ progressText }}</div>
        </div>
        <h5 style="margin-bottom: 12px;">Anteprima (prime 5 righe):</h5>
        <vs-table style="max-width: 100%; overflow-x: auto;">
          <template #thead>
            <vs-tr>
              <vs-th v-for="col in columns" :key="col" style="white-space: nowrap;">{{ col }}</vs-th>
            </vs-tr>
          </template>
          <template #tbody>
<vs-tr v-for="row in parsedRows.slice(0,5)" :key="row">
  <vs-td v-for="col in columns" :key="col" style="white-space: nowrap;">{{ row[col] }}</vs-td>
</vs-tr>
          </template>
        </vs-table>
        <vs-row justify="end" style="margin-top: 20px;">
          <vs-button :loading="importing" @click="importAll" size="large" color="primary" icon>
            <i class="bx bx-import"></i>
            Importa Tutto
          </vs-button>
        </vs-row>
        <div v-if="importResult" style="margin-top: 10px;">
          <vs-alert color="success" icon="check_circle">
            {{ importResult }}
          </vs-alert>
        </div>
        <div v-if="failedPois.length" style="margin-top: 10px;">
          <vs-button color="warning" icon @click="exportFailedPois">
            <i class="bx bx-download"></i>
            Esporta POI falliti (XLSX)
          </vs-button>
        </div>
      </div>
    </div>
    <template #footer>
      <vs-row justify="end">
        <vs-button @click="close" danger size="large">Chiudi</vs-button>
      </vs-row>
    </template>
  </vs-dialog>

    <!-- UUID Prompt Dialog -->
    <vs-dialog v-model="showUuidPrompt" blur not-close>
    <template #header>
      <h4>UUID mancanti o non validi</h4>
    </template>
    <div>
<div v-if="missingId">
  Il file XLSX caricato non contiene la colonna <b>id</b>.<br>
  Vuoi che generiamo automaticamente un UUID per ogni POI e ti forniamo un nuovo XLSX da scaricare?
</div>
<div v-else>
  Le seguenti righe hanno un <b>id</b> non valido (deve essere un UUID):<br>
  <span style="color: #d32f2f;">{{ invalidUuidRows.join(', ') }}</span><br>
  Vuoi che generiamo automaticamente UUID validi per queste righe e ti forniamo un nuovo XLSX da scaricare?
</div>
    </div>
    <template #footer>
      <vs-row justify="end">
        <vs-button @click="generateUuidsAndDownload" color="primary">Sì, genera UUID e scarica XLSX</vs-button>
        <vs-button @click="cancelUuidPrompt" danger>Chiudi</vs-button>
      </vs-row>
    </template>
    </vs-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { apiCall } from '../../utils/ApiMiddleware';

export default {
  name: 'MassivePoiImport',
  props: {
    show: { type: Boolean, required: true },
    onClose: { type: Function, required: true }
  },
  data() {
    return {
      languages: [],
      cities: [],
      targets: [],
      columns: [],
      parsedRows: [],
      parseErrors: [],
      importing: false,
      importResult: null,
      showUuidPrompt: false,
      invalidUuidRows: [],
      missingId: false,
      progress: 0,
      progressText: '',
      failedPois: [],
      showExportFailed: false
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.init();
      }
    }
  },
  methods: {
    exportFailedPois() {
      if (!this.failedPois.length) return;
      // Ensure motivation column is included
      const cols = [...this.columns, 'motivation'];
      const rows = this.failedPois.map(row => {
        const r = {};
        for (const col of this.columns) r[col] = row[col] ?? '';
        r.motivation = row.motivation || '';
        return r;
      });
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: cols });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'POI_Falliti');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'poi_falliti.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    generateUuidsAndDownload() {
      // Generate UUIDs for missing/invalid
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      function uuidv4() {
        // RFC4122 version 4 compliant
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
      // If missingId, add id to all rows
      if (this.missingId) {
        this.parsedRows.forEach(row => { row.id = uuidv4(); });
      } else {
        // Replace invalid UUIDs
        for (let i = 0; i < this.parsedRows.length; i++) {
          if (!uuidRegex.test(this.parsedRows[i].id)) {
            this.parsedRows[i].id = uuidv4();
          }
        }
      }
      // Reorder columns to ensure id is first
      const cols = this.columns;
      const rows = this.parsedRows.map(row => {
        const r = {};
        for (const col of cols) r[col] = row[col] ?? '';
        return r;
      });
      // Create XLSX workbook and sheet
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: cols });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'POIs');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pois_con_uuid.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // Reset state and prompt user to upload new XLSX
      this.showUuidPrompt = false;
      this.parseErrors = [];
      this.parsedRows = [];
      this.missingId = false;
      this.invalidUuidRows = [];
      this.$vs.notification({
        color: 'primary',
        title: 'Scarica e ricarica',
        text: 'UUID generati. Scarica il nuovo XLSX e ricaricalo per continuare.'
      });
    },
    cancelUuidPrompt() {
      this.showUuidPrompt = false;
      this.missingId = false;
      this.invalidUuidRows = [];
    },
    async init() {
      // Only fetch languages
      this.languages = [];
      this.columns = [];
      this.parsedRows = [];
      this.parseErrors = [];
      this.importResult = null;
      const langRes = await apiCall('GET', '/users/languages');
      if (langRes.status === 200) {
        this.languages = langRes.data.language;
      }
      this.columns = [
        'id', 'target', 'country', 'state', 'district', 'city', 'year_built', 'author', 'website', 'street', 'lat', 'lon', 'place_to_discover', 'published'
      ];
      this.languages.forEach(l => {
        this.columns.push(`title_${l.name}`);
        this.columns.push(`description_${l.name}`);
      });
    },
    close() {
      this.$emit('update:show', false);
      this.onClose();
    },
downloadExample() {
  const exampleRow = {};
  this.columns.forEach((col) => {
    if (col === 'id') exampleRow[col] = '123e4567-e89b-12d3-a456-426614174000';
    else if (col === 'target') exampleRow[col] = 'Monumento';
    else if (col === 'country') exampleRow[col] = 'Italia';
    else if (col === 'state') exampleRow[col] = 'Lazio';
    else if (col === 'district') exampleRow[col] = 'Roma';
    else if (col === 'city') exampleRow[col] = 'Roma';
    else if (col === 'year_built') exampleRow[col] = '2020';
    else if (col === 'author') exampleRow[col] = 'Tourial';
    else if (col === 'website') exampleRow[col] = 'https://colosseo.it';
    else if (col === 'street') exampleRow[col] = 'Via Roma 1';
    else if (col === 'lat') exampleRow[col] = '41.9028';
    else if (col === 'lon') exampleRow[col] = '12.4964';
    else if (col === 'place_to_discover') exampleRow[col] = 'TRUE';
    else if (col === 'published') exampleRow[col] = 'FALSE';
    else if (col.startsWith('title_')) exampleRow[col] = `Titolo di esempio (${col.split('_')[1]})`;
    else if (col.startsWith('description_')) exampleRow[col] = `Descrizione di esempio (${col.split('_')[1]})`;
    else exampleRow[col] = '';
  });
  const worksheet = XLSX.utils.json_to_sheet([exampleRow], { header: this.columns });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Esempio');
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'example_pois.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
},
onFileChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'xlsx') {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
        this.handleParsedRows(json);
      } catch (err) {
        this.parseErrors = [err.message];
      }
    };
    reader.onerror = (err) => {
      this.parseErrors = [err.message];
    };
    reader.readAsArrayBuffer(file);
  } else {
    this.parseErrors = ['Formato file non supportato. Usa solo XLSX.'];
  }
},
    handleParsedRows(rows) {
      this.parsedRows = rows;
      this.parseErrors = [];
      this.showUuidPrompt = false;
      this.invalidUuidRows = [];
      this.missingId = false;
      // Validate columns
      const missing = this.columns.filter(c => !(c in this.parsedRows[0]));
      if (missing.length) {
        this.parseErrors.push('Colonne mancanti: ' + missing.join(', '));
      }
      // Check for id column and UUID validity
      if (!this.parsedRows.length) return;
      if (!('id' in this.parsedRows[0])) {
        this.missingId = true;
        this.showUuidPrompt = true;
        return;
      }
      // UUID regex
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      this.invalidUuidRows = [];
      for (let i = 0; i < this.parsedRows.length; i++) {
        const id = this.parsedRows[i].id;
        // Treat empty or invalid UUIDs as invalid
        if (!id || !uuidRegex.test(id)) {
          this.invalidUuidRows.push(i + 2); // +2 for header and 0-index
        }
      }
      if (this.invalidUuidRows.length) {
        this.showUuidPrompt = true;
      }
    },
    async importAll() {
      this.importing = true;
      this.importResult = null;
      this.progress = 0;
      this.progressText = '';
      this.failedPois = [];
      let success = 0, fail = 0;
      let targetsList = [];
      // Fetch first 100 targets (no search param)
      try {
        const targetsRes = await apiCall('GET', '/targets', { limit: 50 });
        if (targetsRes.status === 200 && Array.isArray(targetsRes.data.data)) {
          targetsList = targetsRes.data.data;
        }
      } catch (e) {
        // If targets fetch fails, all will fail
        this.importing = false;
        this.importResult = 'Errore nel recupero dei target.';
        return;
      }
      for (let i = 0; i < this.parsedRows.length; i++) {
        const row = this.parsedRows[i];
        let motivation = '';
        this.progress = Math.round(((i + 1) / this.parsedRows.length) * 100);
        this.progressText = `Elaborazione POI ${i + 1} di ${this.parsedRows.length}`;
        try {
          // 1. Country lookup (by search)
          const countryRes = await apiCall('GET', '/places/countries', { search: row.country });
          const country = (countryRes.status === 200 && countryRes.data.data.length)
            ? countryRes.data.data.find(c => c.name.toLowerCase() === (row.country || '').toLowerCase())
            : null;
          if (!country) {
            motivation = 'Country not found';
            throw new Error(motivation);
          }

          // 2. State lookup (by search, with country_id)
          const stateRes = await apiCall('GET', '/places/states', { country_id: country.id, search: row.state });
          const state = (stateRes.status === 200 && stateRes.data.data.length)
            ? stateRes.data.data.find(s => s.name.toLowerCase() === (row.state || '').toLowerCase())
            : null;
          if (!state) {
            motivation = 'State not found';
            throw new Error(motivation);
          }

          // 3. District lookup (by search, with state_id)
          const districtRes = await apiCall('GET', '/places/districts', { state_id: state.id, search: row.district });
          const district = (districtRes.status === 200 && districtRes.data.data.length)
            ? districtRes.data.data.find(d => d.name.toLowerCase() === (row.district || '').toLowerCase())
            : null;
          if (!district) {
            motivation = 'District not found';
            throw new Error(motivation);
          }

          // 4. City lookup (by search, with district_id)
          const cityRes = await apiCall('GET', '/places/cities', { district_id: district.id, search: row.city });
          const city = (cityRes.status === 200 && cityRes.data.data.length)
            ? cityRes.data.data.find(c => c.name.toLowerCase() === (row.city || '').toLowerCase())
            : null;
          if (!city) {
            motivation = 'City not found';
            throw new Error(motivation);
          }

          // 5. Target lookup (client-side search)
          const target = targetsList.find(t => t.name.toLowerCase() === (row.target || '').toLowerCase());
          if (!target) {
            motivation = 'Target not found';
            throw new Error(motivation);
          }

          // Build POI object
          const poi = {
            id: row.id,
            city_id: city.id,
            target_id: target.id,
            year_built: row.year_built,
            author: row.author,
            website: row.website,
            street: row.street,
            point: { x: parseFloat(row.lon), y: parseFloat(row.lat) },
            place_to_discover: row.place_to_discover === 'TRUE',
            published: row.published === 'TRUE',
            poi_localization_infos: this.languages.map(l => ({
              language_id: l.id,
              name: row[`title_${l.name}`],
              description: row[`description_${l.name}`]
            }))
          };
          // POST
          const res = await apiCall('POST', '/backoffice/pois', poi);
          if (res.status === 200) {
            success++;
          } else {
            motivation = 'POI creation failed';
            throw new Error(motivation);
          }
        } catch (err) {
          fail++;
          // Add motivation to failed POI row
          this.failedPois.push({ ...row, motivation: motivation || (err && err.message) || 'Errore sconosciuto' });
        }
      }
      this.importing = false;
      this.progress = 100;
      this.progressText = 'Importazione completata';
      this.importResult = `Importazione completata: ${success} riuscite, ${fail} fallite.`;
    },
  }
};
</script>
